import React from 'react'
import { GET_MERCHANT_INTEGRATION_SETTINGS } from 'components/Integrations/queries'
import { MerchantIntegrationSettings } from 'components/Integrations/types'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { LoaderProps } from './types'
import { useFetchOnce } from './useFetchOnce'

// Per-merchant integration settings (Lightspeed loyalty config), read by
// ManageLightspeed — shared GraphQL data, so Loader/Context per
// doc/ARCHITECTURE.md §6. Distinct from MerchantIntegrationsLoader, which
// answers which partners are connected.
// The API returns null until the merchant first saves settings. Context null
// means "not fetched yet" (spinner), so store defaults instead.
const EMPTY_SETTINGS: MerchantIntegrationSettings = {
  lightspeedLoyaltyEnabled: false,
  lightspeedLoyaltyUsername: null,
  lightspeedLoyaltyPasswordSet: false
}

export const MerchantIntegrationSettingsLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const {
    merchantIntegrations,
    setMerchantIntegrations,
    setRefetchMerchantIntegrations
  } = useIntegration()

  const { addMessage } = useMessageStore()

  const { isLoaded } = useFetchOnce<{
    getMerchantIntegrations: MerchantIntegrationSettings
  }>({
    query: GET_MERCHANT_INTEGRATION_SETTINGS,
    skip: merchantIntegrations !== null,
    // isLoaded flips true either way, so without this the app renders on a
    // null Context with nothing said.
    onError: () => {
      addMessage({
        type: 'error',
        content: 'Failed to load integration settings'
      })
    },
    onData: (data) => {
      setMerchantIntegrations(data.getMerchantIntegrations ?? EMPTY_SETTINGS)
    },
    onRefetch: (refetch) => {
      const refetchAndSync = async () => {
        const result = (await refetch()) as {
          data?: { getMerchantIntegrations: MerchantIntegrationSettings }
        }
        if (result.data) {
          setMerchantIntegrations(
            result.data.getMerchantIntegrations ?? EMPTY_SETTINGS
          )
        }
        return result
      }

      setRefetchMerchantIntegrations(
        (() => refetchAndSync) as unknown as () => Promise<unknown>
      )
    },
    isParentLoaded
  })

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
